/**
 * Selecting the run to compare the current audit against.
 *
 * Every "vs last scan" figure on the dashboard — the delta badge, the movement
 * row, score changes by tool, fixes resolved, issues resolved — is a diff
 * against ONE previous run. Picking that run wrong does not throw; it silently
 * reports no movement, which reads as "nothing changed" rather than as a bug.
 *
 * Two rules the previous implementation broke:
 *
 * 1. A completed audit auto-saves and is prepended to the history, so
 *    `savedRuns[0]` IS the run on screen. Comparing against it compared the
 *    run with itself and always produced +-0 — which also meant the
 *    "score changes by tool" and "fixes resolved" sections could never render,
 *    since both filter on a non-zero delta.
 * 2. History interleaves domains. Index 0 could be a different site entirely,
 *    so a pixelmojo.io audit could be diffed against avepoint.com.
 *
 * The current run is identified by its STABLE ID, never by array position and
 * never by timestamp equality. Ordering by timestamp is still how "previous"
 * is defined — there is no other ordering available — but identity never is.
 */

import type { SavedRun } from '@/types/radar'

/**
 * Canonical form of a domain for comparison purposes.
 *
 * History legitimately holds both `www.pixelmojo.io` and `pixelmojo.io` as
 * separate rows, entered at different times. They are the same site and must
 * compare against each other, so host equality is decided on the canonical
 * form: lowercased, scheme dropped, `www.` dropped, path/port/trailing dot
 * removed.
 *
 * Deliberately NOT a public-suffix-aware parser: `foo.pixelmojo.io` is a
 * different site and must stay a different site.
 */
export function canonicalDomain(input: string | null | undefined): string {
  if (!input) return ''
  return (
    input
      .trim()
      .toLowerCase()
      .replace(/^[a-z][a-z0-9+.-]*:\/\//, '') // scheme
      .replace(/^[^/?#]*@/, '') // userinfo
      .split(/[/?#]/)[0] // path, query, fragment
      // Port and the fully-qualified trailing dot can appear in either order
      // (`host.:443` and `host:443.`), so neither strip can assume it runs
      // last. Three cheap passes cover both spellings and the plain cases.
      .replace(/\.$/, '')
      .replace(/:\d+$/, '')
      .replace(/\.$/, '')
      .replace(/^www\./, '')
  )
}

/** True when two domain strings name the same site. */
export function isSameDomain(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const ca = canonicalDomain(a)
  return ca !== '' && ca === canonicalDomain(b)
}

/**
 * The most recent completed audit for `activeDomain` that is NOT the run
 * currently on screen, or null when there is no such run.
 *
 * Null is a meaningful answer: a first scan has nothing to compare against,
 * and callers must hide their movement UI rather than render a +-0 that
 * implies a stable score.
 */
export function findPreviousRun(
  runs: readonly SavedRun[],
  activeDomain: string | null | undefined,
  currentRunId: string | null | undefined
): SavedRun | null {
  if (!activeDomain) return null

  // "Previous" must mean EARLIER, not merely "not the current one". Opening a
  // historical audit (History > Load, or the `?run=` deep link) sets
  // currentRunId to an old run; without this bound the newest audit would be
  // returned as its "previous", and the panel would report movement backwards
  // through time under a "since last scan" label.
  //
  // When the current run is not in `runs` yet — the moment between saveRun()
  // recording the id and the history array updating — there is no upper bound
  // to apply, and excluding by id is already correct for that case.
  const currentRun = currentRunId
    ? (runs.find(r => r.id === currentRunId) ?? null)
    : null

  const candidates = runs.filter(
    run =>
      isSameDomain(run.domain, activeDomain) &&
      run.id !== currentRunId &&
      (currentRun === null || run.timestamp < currentRun.timestamp)
  )
  if (candidates.length === 0) return null

  // Newest of what remains. `savedRuns` is already maintained in this order,
  // but the comparison is too load-bearing to inherit an ordering assumption
  // from whoever last touched the array.
  return candidates.reduce((newest, run) =>
    run.timestamp > newest.timestamp ? run : newest
  )
}

/**
 * Checks that were failing in a run — the denominator behind "issues resolved".
 *
 * Mirrors the dashboard's failing threshold (below 50). A tool absent from
 * `toolScores` never ran and is not counted as a failure.
 *
 * The type guard is load-bearing, not defensive: history written before
 * 2026-07-26 stored withheld tools with `score: null`, and `null < 50` is
 * `true` in JavaScript. Without it every withheld check in the previous run
 * counted as a failure and inflated the "issues resolved" denominator — a
 * withheld grade being read as a zero, which is exactly what this codebase
 * forbids.
 */
export function failingCheckCount(run: SavedRun | null): number | null {
  if (!run) return null
  return Object.values(run.toolScores).filter(
    t => typeof t.score === 'number' && t.score < 50
  ).length
}
